import { ref, watch } from 'vue'
import type { Settings } from '@/types/dashboard'
import type { Extension } from '@/types/extension'
import { Statuses } from '@/enum/redesignStatusTypes'
import { getInitialExtensionTime } from '@/util/timeUtils'

/** Whether "show logged out" + "recently logged out" are both enabled. */
export function isRecentlyLoggedOutEnabled (settings: Settings): boolean {
    return !!(settings.showLoggedOutUsers && settings.showRecentlyLoggedOut)
}

/** Early filter: skip logout extensions that already exceeded the max duration. */
export function shouldSkipExpiredLogout (extension: Extension, settings: Settings): boolean {
    return (
        isRecentlyLoggedOutEnabled(settings) &&
        extension.representativeStatus === Statuses.LOGOUT &&
        getInitialExtensionTime(extension, settings) > (settings.recentlyLoggedOutMaxDuration ?? 0)
    )
}

/** Timer check: whether to emit that a logout duration has exceeded the limit. */
export function shouldEmitRecentlyLoggedOutExpired (
    settings: Settings,
    representativeStatus: number,
    elapsedSeconds: number
): boolean {
    return (
        isRecentlyLoggedOutEnabled(settings) &&
        representativeStatus === Statuses.LOGOUT &&
        elapsedSeconds > (settings.recentlyLoggedOutMaxDuration ?? 0)
    )
}

/**
 * Late filter: keep row unless its key is expired and is still LOGOUT.
 * Clears the key on re-login (status no longer LOGOUT).
 */
export function keepAfterExpiredKeys (
    key: string,
    representativeStatus: number | undefined,
    expiredKeys: Set<string>,
    clearExpired: (key: string) => void
): boolean {
    if (!expiredKeys.has(key)) return true
    if (representativeStatus !== Statuses.LOGOUT) {
        clearExpired(key)
        return true
    }
    return false
}

export function useExpiredRecentlyLoggedOutKeys(getSettings: () => Settings) {
    const expiredKeys = ref(new Set<string>())

    function markExpired(key: string) {
        if (!expiredKeys.value.has(key)) {
            expiredKeys.value = new Set([...expiredKeys.value, key])
        }
    }

    function clearExpired(key: string) {
        if (!expiredKeys.value.has(key)) return
        const next = new Set(expiredKeys.value)
        next.delete(key)
        expiredKeys.value = next
    }

    watch(
        () => [
            getSettings().showLoggedOutUsers,
            getSettings().showRecentlyLoggedOut,
            getSettings().recentlyLoggedOutMaxDuration,
        ],
        () => { expiredKeys.value = new Set() }
    )

    return {
        expiredKeys,
        markExpired,
        clearExpired,
    }
}
